import BaseLayout from '@/layout/BaseLayout';
import { trpc } from '@/trpcClient';
import { formatCNPJ } from '@/utils/utils';
import { EMaterialStatus } from '@shelflix/schemas/material/enums';
import Icon from '@shelflix/ui-web/components/Icon';
import { Badge } from '@shelflix/ui-web/components/ui/badge';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent } from '@shelflix/ui-web/components/ui/card';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

export function FacilityDetails() {
  const { uuid: facilityUuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();

  const { data: facility } = useQuery(
    trpc.facility.get.queryOptions(
      {
        uuid: facilityUuid ?? '',
      },
      {
        enabled: !!facilityUuid,
      }
    )
  );

  const downloadContract = async (contractUrl: string | null) => {
    if (!contractUrl) {
      toast.warning('URL do contrato não disponível');
      return;
    }

    try {
      const response = await fetch(contractUrl);
      const blob = await response.blob();

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', '');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      toast.warning('Erro ao baixar o contrato:', error);
    }
  };

  const statusMap = (status: EMaterialStatus) => {
    switch (status) {
      case EMaterialStatus.ACTIVE:
        return 'Ativo';
      case EMaterialStatus.OPERATIONALONLINE:
        return 'Ativo';
      case EMaterialStatus.MAINTENANCE:
        return 'Manutenção';
      case EMaterialStatus.STANDBY:
        return 'Manutenção';
      case EMaterialStatus.OFFLINE:
        return 'Offline';
    }
  };

  const statusStyleMap: Record<EMaterialStatus, { bg: string; text: string }> = {
    [EMaterialStatus.ACTIVE]: {
      bg: 'bg-green-100',
      text: 'text-green-700',
    },
    [EMaterialStatus.OPERATIONALONLINE]: {
      bg: 'bg-green-100',
      text: 'text-green-700',
    },
    [EMaterialStatus.OFFLINE]: {
      bg: 'bg-red-100',
      text: 'text-red-700',
    },
    [EMaterialStatus.MAINTENANCE]: {
      bg: 'bg-yellow-100',
      text: 'text-yellow-800',
    },
    [EMaterialStatus.STANDBY]: {
      bg: 'bg-yellow-100',
      text: 'text-yellow-800',
    },
  };

  return (
    <BaseLayout>
      <div className="flex flex-col gap-8">
        {!facility ? (
          <p>Carregando...</p>
        ) : (
          <Card className="mx-auto w-full overflow-hidden rounded-[5px]">
            <CardContent className="p-0">
              <div className="flex flex-row gap-6 p-6 md:flex-row">
                <div className="h-[166px] w-[166px] overflow-hidden rounded-[5px]">
                  <img className="h-full w-full object-cover" alt="Establishment logo" src={facility.logoUrl} />
                </div>

                <div className="flex-1">
                  <h2 className="text-primary mb-6 font-['Poppins',Helvetica] text-lg leading-5 font-semibold">
                    {facility.name}
                  </h2>

                  <div className="flex flex-wrap gap-8">
                    <div className="flex max-w-[359px] flex-col gap-8">
                      <div className="flex flex-col gap-1">
                        <span className="font-['Poppins',Helvetica] text-xs leading-5 font-semibold text-[var(--label-color)]">
                          CNPJ
                        </span>
                        <span className="font-['Figtree',Helvetica] text-sm leading-5 font-medium text-[var(--label-color)]">
                          {formatCNPJ(facility.cnpj)}
                        </span>
                      </div>

                      <div className="flex flex-col gap-1">
                        <span className="font-['Poppins',Helvetica] text-xs leading-5 font-semibold text-[var(--label-color)]">
                          Endereço
                        </span>
                        <p className="font-['Figtree',Helvetica] text-sm leading-5 font-medium whitespace-pre-line text-[var(--label-color)]">
                          {facility.address}, {facility.number}, {facility.district}, {facility.city}-{facility.state}
                        </p>
                      </div>
                    </div>

                    <div className="max-w-[359px]">
                      <div className="flex flex-col gap-1">
                        <span className="font-['Poppins',Helvetica] text-xs leading-5 font-semibold text-[var(--label-color)]">
                          Responsável
                        </span>
                        <span className="font-['Figtree',Helvetica] text-sm leading-5 font-medium text-[var(--label-color)]">
                          {facility.responsibleUser.name}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="border-primary text-primary hover:text-primary h-[42px] self-start font-['Figtree',Helvetica] text-sm font-semibold"
                  onClick={() => navigate(`/facility/edit/${facility.uuid}`)}
                >
                  <Icon name="edit" fill={1} />
                  Atualizar dados
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <section className="w-full rounded-[5px] p-7">
          <div className="mb-6 flex flex-wrap items-center justify-between">
            <h2 className="text-primary font-['Poppins',Helvetica] text-2xl leading-5 font-semibold">
              Materiais vinculados
            </h2>

            <Button className="bg-primary hover:bg-primary text-white" onClick={() => navigate('/material')}>
              <Icon name="connected_tv" />
              <span className="font-['Figtree',Helvetica] text-sm font-semibold">Vincular novo material</span>
            </Button>
          </div>

          <div className="flex flex-wrap gap-4">
            {facility &&
              facility.material.map((material) => (
                <Card key={material.uuid} className="rounded-[5px] border border-solid border-[#d9d9d9]">
                  <CardContent className="min-w-[432px]">
                    <div className="mb-2 flex items-start justify-between">
                      <div>
                        <h3 className="font-['Poppins',Helvetica] text-sm font-semibold text-[#5d5d5d]">
                          {material.code}
                        </h3>
                        <p className="font-['Figtree',Helvetica] text-base text-[#5d5d5d]">{material.name}</p>
                      </div>
                      <Badge
                        className={`${statusStyleMap[material.status].bg} ${statusStyleMap[material.status].text} rounded-sm font-['Figtree',Helvetica] text-xs font-medium`}
                      >
                        {statusMap(material.status)}
                      </Badge>
                    </div>

                    <div className="flex items-center justify-between">
                      <p className="font-['Figtree',Helvetica] text-base text-[#8c8787]">ÚItima conexão - 10/12/2025</p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 rounded-[3px] border-[#a1a1a1] py-0.5 text-[#a1a1a1]"
                        onClick={() => downloadContract(material.contractUrl)}
                      >
                        <Icon name="file_download" size={18} />
                        <span className="font-['Poppins',Helvetica] text-xs font-medium">Contrato</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
        </section>
      </div>
    </BaseLayout>
  );
}
